import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Trash2, Flag, Clock, X, CheckCircle2, CalendarDays, MapPin, Plus, Zap } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

const field = "w-full bg-[#0F1812] border border-[#00E676]/20 rounded-lg px-3 py-2 text-white placeholder-[#697C6F] focus:outline-none focus:ring-2 focus:ring-[#39FF14]";
const todayISO = () => new Date().toISOString().slice(0, 10);

export default function AdminGame() {
  const [dates, setDates] = useState([]);
  const [selDate, setSelDate] = useState("");
  const [courses, setCourses] = useState([]);
  const [settleFor, setSettleFor] = useState(null);
  const [arrivee, setArrivee] = useState("");
  const [progDate, setProgDate] = useState(todayISO());
  const [progReunions, setProgReunions] = useState(null);
  const [loadingProg, setLoadingProg] = useState(false);
  const [importingR, setImportingR] = useState(null);

  const loadDates = () => api.get("/game/dates").then((r) => setDates(r.data)).catch(() => {});
  const load = (date) => api.get("/game/courses", { params: date ? { date } : {} }).then((r) => setCourses(r.data));

  useEffect(() => { loadDates(); load(); }, []); // eslint-disable-line

  const remove = async (id) => {
    await api.delete(`/admin/game/${id}`);
    toast.success("Course supprimée");
    load(selDate);
  };

  const loadProgram = async () => {
    setLoadingProg(true);
    setProgReunions(null);
    try {
      const res = await api.get("/admin/game/program", { params: { date: progDate } });
      setProgReunions(res.data.reunions);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoadingProg(false);
    }
  };

  const importReunion = async (rn) => {
    setImportingR(rn);
    try {
      const res = await api.post("/admin/game/import-reunion", { date: progDate, reunion_num: rn });
      toast.success(`${res.data.imported} course(s) importée(s) — ${res.data.reunion} ${res.data.hippodrome}`);
      setSelDate(progDate);
      await loadDates();
      await load(progDate);
      await loadProgram();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setImportingR(null);
    }
  };

  const settle = async () => {
    try {
      const nums = arrivee.split(/[\s,]+/).filter(Boolean).map((n) => parseInt(n));
      await api.post(`/admin/game/${settleFor.id}/settle`, { arrivee: nums });
      toast.success("Arrivée enregistrée, points attribués !");
      setSettleFor(null);
      setArrivee("");
      load(selDate);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const [autoId, setAutoId] = useState(null);
  const [autoAll, setAutoAll] = useState(false);

  const settleAuto = async (id) => {
    setAutoId(id);
    try {
      const res = await api.post(`/admin/game/${id}/settle-auto`);
      toast.success(`Arrivée Geny : ${res.data.arrivee.join("-")} — points attribués !`);
      load(selDate);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setAutoId(null);
    }
  };

  const settleAutoAll = async () => {
    setAutoAll(true);
    try {
      const res = await api.post("/admin/game/settle-auto-all", null, { params: { date: selDate || undefined } });
      toast.success(`${res.data.settled}/${res.data.checked} course(s) clôturée(s) automatiquement`);
      load(selDate);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setAutoAll(false);
    }
  };

  const grouped = [];
  const seen = {};
  courses.forEach((c) => {
    if (!seen[c.reunion_num]) { seen[c.reunion_num] = { key: c.reunion_num, label: c.reunion, hippodrome: c.hippodrome, items: [] }; grouped.push(seen[c.reunion_num]); }
    seen[c.reunion_num].items.push(c);
  });

  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-[#00E676]/15 bg-[#131F17] p-6 space-y-4" data-testid="program-browser">
        <h2 className="text-lg font-bold flex items-center gap-2"><CalendarDays size={18} className="text-[#00E676]" /> Ajouter les réunions du jour</h2>
        <p className="text-sm text-[#A1B5A8]">Choisissez une date, chargez le programme Geny, puis importez chaque réunion voulue en un clic (courses + partants inclus).</p>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="text-xs text-[#A1B5A8] block mb-1">Date du programme</label>
            <input type="date" className={`${field} w-auto`} value={progDate} onChange={(e) => setProgDate(e.target.value)} data-testid="program-date" />
          </div>
          <button onClick={loadProgram} disabled={loadingProg} data-testid="load-program-btn" className="px-4 py-2 rounded-lg font-bold text-[#04180a] bg-gradient-to-r from-[#00E676] to-[#39FF14] disabled:opacity-60 flex items-center gap-2">
            {loadingProg ? <><Loader2 className="animate-spin" size={16} /> Chargement…</> : "Charger les réunions"}
          </button>
        </div>

        {progReunions && (
          progReunions.length === 0 ? (
            <p className="text-[#697C6F] text-sm">Aucune réunion trouvée pour cette date.</p>
          ) : (
            <div className="grid sm:grid-cols-2 gap-3" data-testid="program-reunions">
              {progReunions.map((r) => {
                const done = r.nb_imported >= r.nb_courses && r.nb_courses > 0;
                return (
                  <div key={r.reunion_num} className="rounded-lg border border-[#00E676]/15 bg-[#0F1812] p-4 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-bold flex items-center gap-2">
                        <span className="text-[#39FF14]">{r.reunion}</span>
                        <MapPin size={14} className="text-[#00E676]" />
                        <span className="truncate">{r.hippodrome}</span>
                      </div>
                      <div className="text-xs text-[#697C6F]">{r.discipline} · {r.nb_courses} courses{r.nb_imported > 0 ? ` · ${r.nb_imported} importées` : ""}</div>
                    </div>
                    <button
                      onClick={() => importReunion(r.reunion_num)}
                      disabled={importingR === r.reunion_num}
                      data-testid={`import-reunion-${r.reunion_num}`}
                      className={`shrink-0 text-xs font-bold px-3 py-2 rounded-md flex items-center gap-1 ${done ? "bg-[#1A2B20] text-[#39FF14]" : "bg-[#00E676] text-[#04180a]"} disabled:opacity-60`}
                    >
                      {importingR === r.reunion_num ? <Loader2 className="animate-spin" size={13} /> : done ? <><CheckCircle2 size={13} /> Réimporter</> : <><Plus size={13} /> Importer</>}
                    </button>
                  </div>
                );
              })}
            </div>
          )
        )}
      </div>

      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-lg font-bold">Programme en ligne</h2>
        <div className="flex items-center gap-2">
          <button onClick={settleAutoAll} disabled={autoAll} data-testid="auto-settle-all-btn" className="text-xs font-bold px-3 py-2 rounded-md bg-[#00E676] text-[#04180a] flex items-center gap-1 disabled:opacity-60">
            {autoAll ? <Loader2 className="animate-spin" size={14} /> : <Zap size={14} />} Résultats auto (journée)
          </button>
          <select className={`${field} w-auto`} value={selDate} onChange={(e) => { setSelDate(e.target.value); load(e.target.value); }} data-testid="game-admin-date">
            <option value="">Aujourd'hui</option>
            {dates.map((d) => <option key={d} value={d}>{new Date(d).toLocaleDateString("fr-FR")}</option>)}
          </select>
        </div>
      </div>

      {grouped.length === 0 && <p className="text-[#697C6F] text-sm">Aucune course en ligne pour cette date.</p>}
      {grouped.map((g) => (
        <div key={g.key}>
          <h3 className="text-sm font-bold text-[#39FF14] mb-2">{g.label} — {g.hippodrome}</h3>
          <div className="space-y-2">
            {g.items.map((c) => (
              <div key={c.id} className="rounded-lg border border-[#00E676]/15 bg-[#131F17] p-4 flex items-center justify-between gap-3" data-testid={`game-admin-course-${c.id}`}>
                <div className="min-w-0">
                  <div className="font-bold flex items-center gap-2 flex-wrap">
                    <span className="text-[#39FF14] text-xs">{c.course}</span>
                    <span className="truncate">{c.nom_prix}</span>
                    {c.heure && <span className="text-xs text-[#697C6F] flex items-center gap-1"><Clock size={11} />{c.heure}</span>}
                    <span className="text-xs text-[#697C6F]">{(c.partants || []).length} partants</span>
                  </div>
                  {c.status === "settled" && <div className="text-xs text-[#00E676] mt-1">Arrivée : {c.arrivee?.join("-")}</div>}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {c.status !== "settled" ? (
                    <>
                      <button onClick={() => settleAuto(c.id)} disabled={autoId === c.id} data-testid={`game-auto-${c.id}`} className="text-xs font-bold px-3 py-1.5 rounded-md bg-[#00E676] text-[#04180a] flex items-center gap-1 disabled:opacity-60">
                        {autoId === c.id ? <Loader2 className="animate-spin" size={13} /> : <Zap size={13} />} Auto
                      </button>
                      <button onClick={() => { setSettleFor(c); setArrivee(""); }} data-testid={`game-settle-${c.id}`} className="text-xs font-bold px-3 py-1.5 rounded-md bg-[#1A2B20] text-[#39FF14] flex items-center gap-1"><Flag size={13} /> Manuel</button>
                    </>
                  ) : (
                    <span className="text-xs text-[#39FF14] flex items-center gap-1"><CheckCircle2 size={14} /> Terminé</span>
                  )}
                  <button onClick={() => remove(c.id)} className="p-1.5 rounded-md text-[#FF3B30] hover:bg-[#FF3B30]/10"><Trash2 size={15} /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {settleFor && (
        <div className="fixed inset-0 z-[60] grid place-items-center bg-black/70 p-4" onClick={() => setSettleFor(null)}>
          <div className="w-full max-w-md rounded-xl border border-[#00E676]/30 bg-[#131F17] p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold mb-1">Arrivée officielle</h3>
            <p className="text-sm text-[#A1B5A8] mb-4">{settleFor.hippodrome} — {settleFor.course} {settleFor.nom_prix}</p>
            <input autoFocus className={field} placeholder="Ex : 7 3 12 5 (dans l'ordre)" value={arrivee} onChange={(e) => setArrivee(e.target.value)} data-testid="game-arrivee-input" />
            <p className="text-xs text-[#697C6F] mt-2">Le gagnant est le 1er numéro. Les points des joueurs sont attribués automatiquement.</p>
            <div className="flex gap-2 mt-5">
              <button onClick={() => setSettleFor(null)} className="flex-1 py-2.5 rounded-lg bg-[#1A2B20] text-white">Annuler</button>
              <button onClick={settle} data-testid="game-confirm-settle" className="flex-1 py-2.5 rounded-lg font-bold text-[#04180a] bg-gradient-to-r from-[#00E676] to-[#39FF14]">Valider</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
